import { Box, Text } from '@chakra-ui/react'
import type { HelpdeskRequest } from '@/types/helpdesk'
import { TimerCell } from '../Cell/TimerCell'
import { QuickAcknowledgeButton } from '../QuickAcknowledgeButton'
import { DescriptionHoverCell } from '../Cell/DescriptionHoverCell'

type Props = {
  data: HelpdeskRequest[]
  onAcknowledge: (id: string) => void | Promise<void>
  loadingId?: string | null
  height?: string | number
}

// Flexible grid columns with minmax for responsiveness
const COLS =
  '110px minmax(160px, 1.2fr) 130px minmax(220px, 1.8fr) minmax(220px, 1.3fr) 110px 180px'

// Minimum table width to prevent squishing
const MIN_TABLE_W = '1150px'

export function UrgentNewQueue({
  data,
  onAcknowledge,
  loadingId,
  height = '100%',
}: Props) {
  return (
    <Box
      bg="white"
      borderRadius="md"
      border="1px solid"
      borderColor="gray.200"
      overflow="hidden"
      h={height}
      minW={0}
      display="flex"
      flexDirection="column"
    >
      <Box flex="1" minH={0} overflowX="auto" overflowY="hidden" minW={0}>
        <Box minW={MIN_TABLE_W} h="100%">
          <Box overflowY="auto" maxH="100%" sx={{ scrollBehavior: 'smooth' }}>
            {/* Header */}
            <Box
              position="sticky"
              top={0}
              zIndex={2}
              display="grid"
              gridTemplateColumns={COLS}
              px={5}
              py={3}
              bg="white"
              fontSize="sm"
              fontWeight="semibold"
              borderBottom="1px solid"
              borderColor="gray.200"
            >
              <Box>Request ID</Box>
              <Box>Client</Box>
              <Box>Request Type</Box>
              <Box>Subject</Box>
              <Box>Date and Time of Request</Box>
              <Box>Timer</Box>
              <Box textAlign="center">Quick Action</Box>
            </Box>

            {data.map((r) => (
              <Box
                key={r.id}
                display="grid"
                gridTemplateColumns={COLS}
                px={5}
                py={4}
                borderBottom="1px solid"
                borderColor="gray.100"
                fontSize="sm"
                alignItems="center"
                _hover={{ bg: 'gray.50' }}
              >
                <Box>{r.id}</Box>

                <Box minW={0}>
                  <Text fontWeight="semibold" noOfLines={1}>
                    {r.clientName}
                  </Text>
                  <Text fontSize="xs" color="gray.500" noOfLines={1}>
                    {r.clientOrg ?? '-'}
                  </Text>
                </Box>

                <Box>{r.requestType}</Box>

                <Box minW={0}>
                  <DescriptionHoverCell
                    subject={r.subject}
                    headerText={`${r.clientName} submitted a new ticket`}
                    description={r.description}
                  />
                </Box>

                <Box minW={0} whiteSpace="nowrap">
                  {new Date(r.requestedAt).toLocaleString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Box>

                <Box>
                  <TimerCell requestedAt={r.requestedAt} />
                </Box>

                <Box display="flex" justifyContent="center">
                  <QuickAcknowledgeButton
                    onAcknowledge={() => onAcknowledge(r.id)}
                    isLoading={loadingId === r.id}
                  />
                </Box>
              </Box>
            ))}

            {data.length === 0 && (
              <Box px={5} py={10} textAlign="center" color="gray.500">
                No Record Found
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}